/**
 * Runtime Configuration Module
 * Fetches configuration from /runtime-config at runtime (not build time)
 * Uses singleton pattern with caching for optimal performance
 */

export interface RuntimeConfig {
  apiUrl: string;
}

let configCache: RuntimeConfig | null = null;
let configPromise: Promise<RuntimeConfig> | null = null;

/**
 * Fetches runtime configuration from the server
 * Results are cached after the first fetch
 * Safe to call from both server and client side
 */
export async function getRuntimeConfig(): Promise<RuntimeConfig> {
  const isBrowser = typeof window !== 'undefined';
  
  if (configCache) {
    return configCache;
  }

  if (configPromise) {
    return configPromise;
  }

  if (!isBrowser) {
    configCache = {
      apiUrl: process.env.API_URL || 'http://localhost:8001',
    };
    return configCache;
  }

  configPromise = fetch('/runtime-config')
    .then((res) => {
      if (!res.ok) {
        throw new Error(`Failed to fetch runtime config: ${res.status}`);
      }
      return res.json();
    })
    .then((config: RuntimeConfig) => {
      configCache = config;
      configPromise = null;
      return config;
    })
    .catch((error) => {
      configPromise = null;
      console.error('[ERROR] Failed to fetch runtime config:', error);
      
      const fallback: RuntimeConfig = {
        apiUrl: 'http://localhost:8001',
      };
      
      configCache = fallback;
      return fallback;
    });

  return configPromise;
}

/**
 * Get the API URL from runtime configuration
 */
export async function getRuntimeApiUrl(): Promise<string> {
  const config = await getRuntimeConfig();
  return config.apiUrl;
}
